import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiErrorMessage, jagranNextService, unwrapList } from "../../apiService/jagranNext.service";
import {
  DangerButton,
  EmptyState,
  Field,
  formatWhen,
  inputClass,
  Notice,
  PAGE_SIZE,
  PageShell,
  Pager,
  Panel,
  PrimaryButton,
  registrationSummary,
  SecondaryButton,
  StatusBadge,
  toDateTimeInput,
  toIso,
} from "./ui";

const FALLBACK_FIELD_TYPES = ["text", "email", "phone", "number", "select", "radio", "checkbox", "textarea", "date"];

const emptySeason = { name: "", price: "", start_date: "", end_date: "", status: "upcoming" };

const emptyPromo = {
  code: "",
  description: "",
  discount_type: "percentage",
  discount_value: "",
  valid_from: "",
  valid_until: "",
  max_uses: "",
  is_active: true,
};

const emptyField = {
  name: "",
  label: "",
  type: "text",
  is_required: true,
  sort_order: 10,
  placeholder: "",
  help_text: "",
  options: "",
};

function normalizeFieldTypes(body) {
  const list = Array.isArray(body) ? body : body?.data || body?.types || [];
  const names = list
    .map((item) => (typeof item === "string" ? item : item?.type || item?.name || item?.value))
    .filter(Boolean);
  return names.length ? names : FALLBACK_FIELD_TYPES;
}

export default function SeasonDetail() {
  const { campaignId, seasonId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("details");
  const [season, setSeason] = useState(null);
  const [form, setForm] = useState(emptySeason);
  const [promoForm, setPromoForm] = useState(emptyPromo);
  const [promos, setPromos] = useState([]);
  const [fieldForm, setFieldForm] = useState(emptyField);
  const [fields, setFields] = useState([]);
  const [fieldTypes, setFieldTypes] = useState(FALLBACK_FIELD_TYPES);
  const [registrations, setRegistrations] = useState([]);
  const [registrationOffset, setRegistrationOffset] = useState(0);
  const [registrationTotal, setRegistrationTotal] = useState(null);
  const [loadedTabs, setLoadedTabs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    async function loadSeason() {
      setLoading(true);
      setError("");
      try {
        const response = await jagranNextService.getSeason(campaignId, seasonId);
        if (!active) return;
        const nextSeason = response.data || null;
        setSeason(nextSeason);
        setForm({
          name: nextSeason?.name || "",
          price: nextSeason?.price ?? "",
          start_date: toDateTimeInput(nextSeason?.start_date),
          end_date: toDateTimeInput(nextSeason?.end_date),
          status: nextSeason?.status || "upcoming",
        });
      } catch (requestError) {
        if (active) setError(getApiErrorMessage(requestError));
      } finally {
        if (active) setLoading(false);
      }
    }
    loadSeason();
    return () => {
      active = false;
    };
  }, [campaignId, seasonId]);

  useEffect(() => {
    if (tab === "details" || loadedTabs[tab]) return undefined;
    let active = true;

    async function loadTab() {
      setError("");
      try {
        if (tab === "promos") {
          const response = await jagranNextService.listPromos(campaignId, seasonId, { limit: 100, offset: 0 });
          if (active) setPromos(unwrapList(response.data).rows);
        }
        if (tab === "fields") {
          const [fieldResponse, typeResponse] = await Promise.all([
            jagranNextService.listFields(campaignId, seasonId, { limit: 100, offset: 0 }),
            jagranNextService.listFieldTypes().catch(() => ({ data: FALLBACK_FIELD_TYPES })),
          ]);
          if (!active) return;
          setFields(unwrapList(fieldResponse.data).rows);
          setFieldTypes(normalizeFieldTypes(typeResponse.data));
        }
        if (tab === "leads") {
          const response = await jagranNextService.listSeasonRegistrations(campaignId, seasonId, {
            limit: PAGE_SIZE,
            offset: registrationOffset,
          });
          if (!active) return;
          const list = unwrapList(response.data);
          setRegistrations(list.rows);
          setRegistrationTotal(list.total);
        }
        if (active) setLoadedTabs((current) => ({ ...current, [tab]: true }));
      } catch (requestError) {
        if (active) setError(getApiErrorMessage(requestError));
      }
    }

    loadTab();
    return () => {
      active = false;
    };
  }, [tab, campaignId, seasonId, loadedTabs, registrationOffset]);

  const updateSeason = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await jagranNextService.updateSeason(campaignId, seasonId, {
        name: form.name.trim(),
        price: Number(form.price),
        start_date: toIso(form.start_date),
        end_date: toIso(form.end_date),
        status: form.status,
      });
      setSeason(response.data || season);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const savePromo = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const body = {
      code: promoForm.code.trim(),
      description: promoForm.description.trim(),
      discount_type: promoForm.discount_type,
      discount_value: Number(promoForm.discount_value),
      valid_from: toIso(promoForm.valid_from),
      valid_until: toIso(promoForm.valid_until),
      max_uses: promoForm.max_uses === "" ? null : Number(promoForm.max_uses),
      is_active: promoForm.is_active,
    };
    try {
      if (promoForm.id) {
        await jagranNextService.updatePromo(campaignId, seasonId, promoForm.id, body);
      } else {
        await jagranNextService.createPromo(campaignId, seasonId, body);
      }
      const response = await jagranNextService.listPromos(campaignId, seasonId, { limit: 100, offset: 0 });
      setPromos(unwrapList(response.data).rows);
      setPromoForm(emptyPromo);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const removePromo = async (promo) => {
    if (!window.confirm(`Delete promo ${promo.code}?`)) return;
    try {
      await jagranNextService.deletePromo(campaignId, seasonId, promo.id);
      setPromos((current) => current.filter((item) => item.id !== promo.id));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    }
  };

  const saveField = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const options = fieldForm.options
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    const body = {
      name: fieldForm.name.trim(),
      label: fieldForm.label.trim(),
      type: fieldForm.type,
      is_required: fieldForm.is_required,
      sort_order: Number(fieldForm.sort_order) || 0,
      placeholder: fieldForm.placeholder.trim(),
      help_text: fieldForm.help_text.trim(),
    };
    if (options.length) body.options = options;
    try {
      if (fieldForm.id) {
        await jagranNextService.updateField(campaignId, seasonId, fieldForm.id, body);
      } else {
        await jagranNextService.createField(campaignId, seasonId, body);
      }
      const response = await jagranNextService.listFields(campaignId, seasonId, { limit: 100, offset: 0 });
      setFields(unwrapList(response.data).rows);
      setFieldForm(emptyField);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const removeField = async (field) => {
    if (!window.confirm(`Delete field ${field.label || field.name}?`)) return;
    try {
      await jagranNextService.deleteField(campaignId, seasonId, field.id);
      setFields((current) => current.filter((item) => item.id !== field.id));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    }
  };

  const removeRegistration = async (registration) => {
    if (!window.confirm("Delete this registration?")) return;
    try {
      await jagranNextService.deleteRegistration(campaignId, seasonId, registration.id);
      setRegistrations((current) => current.filter((item) => item.id !== registration.id));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    }
  };

  const tabs = [
    ["details", "Details"],
    ["promos", "Promo codes"],
    ["fields", "Registration form"],
    ["leads", "Leads"],
  ];

  return (
    <PageShell
      title={season?.name || "Season"}
      description="Promo codes, registration fields, and leads for this season."
      actions={
        <SecondaryButton onClick={() => navigate(`/products/jagran-next/campaigns/${campaignId}`)}>
          Back to campaign
        </SecondaryButton>
      }
    >
      <div className="mb-5 flex gap-2 rounded-xl bg-white p-1 shadow-sm ring-1 ring-gray-200">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${
              tab === id ? "bg-red-600 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <Notice>{error}</Notice>
      {loading ? <p className="text-sm text-gray-500">Loading season...</p> : null}

      {tab === "details" && !loading ? (
        <Panel title="Season details">
          <form className="grid gap-4 md:grid-cols-2" onSubmit={updateSeason}>
            <Field label="Name" required>
              <input className={inputClass} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
            </Field>
            <Field label="Price" required>
              <input className={inputClass} type="number" min="0" step="0.01" value={form.price} onChange={(event) => setForm({ ...form, price: event.target.value })} required />
            </Field>
            <Field label="Starts" required>
              <input className={inputClass} type="datetime-local" value={form.start_date} onChange={(event) => setForm({ ...form, start_date: event.target.value })} required />
            </Field>
            <Field label="Ends" required>
              <input className={inputClass} type="datetime-local" value={form.end_date} onChange={(event) => setForm({ ...form, end_date: event.target.value })} required />
            </Field>
            <Field label="Status">
              <select className={inputClass} value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                <option value="upcoming">Upcoming</option>
                <option value="active">Active</option>
                <option value="ended">Ended</option>
              </select>
            </Field>
            <div className="flex items-end">
              <PrimaryButton type="submit" disabled={saving}>{saving ? "Saving..." : "Save season"}</PrimaryButton>
            </div>
          </form>
        </Panel>
      ) : null}

      {tab === "promos" ? (
        <div className="space-y-4">
          <Panel title={promoForm.id ? "Edit promo code" : "New promo code"} description="Percentage or a fixed rupee discount. Leave max uses empty for unlimited.">
            <form className="grid gap-4 md:grid-cols-2" onSubmit={savePromo}>
              <Field label="Code" required>
                <input className={inputClass} placeholder="EARLYBIRD" value={promoForm.code} onChange={(event) => setPromoForm({ ...promoForm, code: event.target.value })} required />
              </Field>
              <Field label="Description">
                <input className={inputClass} value={promoForm.description} onChange={(event) => setPromoForm({ ...promoForm, description: event.target.value })} />
              </Field>
              <Field label="Discount type">
                <select className={inputClass} value={promoForm.discount_type} onChange={(event) => setPromoForm({ ...promoForm, discount_type: event.target.value })}>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed amount</option>
                </select>
              </Field>
              <Field label="Discount value" required>
                <input className={inputClass} type="number" min="0" step="0.01" value={promoForm.discount_value} onChange={(event) => setPromoForm({ ...promoForm, discount_value: event.target.value })} required />
              </Field>
              <Field label="Valid from" required>
                <input className={inputClass} type="datetime-local" value={promoForm.valid_from} onChange={(event) => setPromoForm({ ...promoForm, valid_from: event.target.value })} required />
              </Field>
              <Field label="Valid until" required>
                <input className={inputClass} type="datetime-local" value={promoForm.valid_until} onChange={(event) => setPromoForm({ ...promoForm, valid_until: event.target.value })} required />
              </Field>
              <Field label="Max uses" hint="Blank means unlimited.">
                <input className={inputClass} type="number" min="1" value={promoForm.max_uses} onChange={(event) => setPromoForm({ ...promoForm, max_uses: event.target.value })} />
              </Field>
              <label className="flex items-end gap-2 pb-3 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={promoForm.is_active} onChange={(event) => setPromoForm({ ...promoForm, is_active: event.target.checked })} />
                Active
              </label>
              <div className="flex gap-2 md:col-span-2">
                <PrimaryButton type="submit" disabled={saving}>{promoForm.id ? "Update promo" : "Add promo"}</PrimaryButton>
                {promoForm.id ? <SecondaryButton onClick={() => setPromoForm(emptyPromo)}>Cancel</SecondaryButton> : null}
              </div>
            </form>
          </Panel>
          {promos.length === 0 ? <EmptyState title="No promo codes yet" /> : null}
          {promos.length > 0 ? (
            <Panel>
              {promos.map((promo) => (
                <div key={promo.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 py-3 text-sm last:border-0">
                  <div>
                    <p className="font-semibold text-gray-900">{promo.code}</p>
                    <p className="text-gray-500">{promo.discount_type} {promo.discount_value} · uses {promo.used_count ?? 0}/{promo.max_uses ?? "unlimited"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge value={promo.is_active ? "active" : "closed"} />
                    <SecondaryButton
                      onClick={() =>
                        setPromoForm({
                          id: promo.id,
                          code: promo.code || "",
                          description: promo.description || "",
                          discount_type: promo.discount_type || "percentage",
                          discount_value: promo.discount_value ?? "",
                          valid_from: toDateTimeInput(promo.valid_from),
                          valid_until: toDateTimeInput(promo.valid_until),
                          max_uses: promo.max_uses ?? "",
                          is_active: Boolean(promo.is_active),
                        })
                      }
                    >
                      Edit
                    </SecondaryButton>
                    <DangerButton onClick={() => removePromo(promo)}>Delete</DangerButton>
                  </div>
                </div>
              ))}
            </Panel>
          ) : null}
        </div>
      ) : null}

      {tab === "fields" ? (
        <div className="space-y-4">
          <Panel title={fieldForm.id ? "Edit field" : "New registration field"}>
            <form className="grid gap-4 md:grid-cols-2" onSubmit={saveField}>
              <Field label="Name" required>
                <input className={inputClass} placeholder="full_name" value={fieldForm.name} onChange={(event) => setFieldForm({ ...fieldForm, name: event.target.value })} required />
              </Field>
              <Field label="Label" required>
                <input className={inputClass} placeholder="Full Name" value={fieldForm.label} onChange={(event) => setFieldForm({ ...fieldForm, label: event.target.value })} required />
              </Field>
              <Field label="Type">
                <select className={inputClass} value={fieldForm.type} onChange={(event) => setFieldForm({ ...fieldForm, type: event.target.value })}>
                  {fieldTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </Field>
              <Field label="Sort order">
                <input className={inputClass} type="number" value={fieldForm.sort_order} onChange={(event) => setFieldForm({ ...fieldForm, sort_order: event.target.value })} />
              </Field>
              <Field label="Placeholder">
                <input className={inputClass} value={fieldForm.placeholder} onChange={(event) => setFieldForm({ ...fieldForm, placeholder: event.target.value })} />
              </Field>
              <Field label="Help text">
                <input className={inputClass} value={fieldForm.help_text} onChange={(event) => setFieldForm({ ...fieldForm, help_text: event.target.value })} />
              </Field>
              <Field className="md:col-span-2" label="Options" hint="Comma separated, for select or radio fields.">
                <input className={inputClass} placeholder="beginner, intermediate, expert" value={fieldForm.options} onChange={(event) => setFieldForm({ ...fieldForm, options: event.target.value })} />
              </Field>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={fieldForm.is_required} onChange={(event) => setFieldForm({ ...fieldForm, is_required: event.target.checked })} />
                Required
              </label>
              <div className="flex gap-2 md:col-span-2">
                <PrimaryButton type="submit" disabled={saving}>{fieldForm.id ? "Update field" : "Add field"}</PrimaryButton>
                {fieldForm.id ? <SecondaryButton onClick={() => setFieldForm(emptyField)}>Cancel</SecondaryButton> : null}
              </div>
            </form>
          </Panel>
          {fields.length === 0 ? <EmptyState title="No registration fields yet" /> : null}
          {fields.length > 0 ? (
            <Panel>
              {fields.map((field) => (
                <div key={field.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 py-3 text-sm last:border-0">
                  <div>
                    <p className="font-semibold text-gray-900">{field.label || field.name}</p>
                    <p className="text-gray-500">{field.name} · {field.type} · order {field.sort_order ?? "-"}</p>
                  </div>
                  <div className="flex gap-2">
                    <SecondaryButton
                      onClick={() =>
                        setFieldForm({
                          id: field.id,
                          name: field.name || "",
                          label: field.label || "",
                          type: field.type || "text",
                          is_required: Boolean(field.is_required),
                          sort_order: field.sort_order ?? 0,
                          placeholder: field.placeholder || "",
                          help_text: field.help_text || "",
                          // the API returns options as { label, value } objects
                          options: Array.isArray(field.options)
                            ? field.options
                                .map((option) => (typeof option === "object" && option !== null ? option.label ?? option.value : option))
                                .join(", ")
                            : "",
                        })
                      }
                    >
                      Edit
                    </SecondaryButton>
                    <DangerButton onClick={() => removeField(field)}>Delete</DangerButton>
                  </div>
                </div>
              ))}
            </Panel>
          ) : null}
        </div>
      ) : null}

      {tab === "leads" ? (
        <Panel>
          {registrations.length === 0 ? <EmptyState title="No leads for this season" /> : null}
          {registrations.length > 0 ? (
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-3">Lead</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Created</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {registrations.map((registration) => (
                  <tr key={registration.id}>
                    <td className="px-3 py-3 text-gray-800">{registrationSummary(registration)}</td>
                    <td className="px-3 py-3"><StatusBadge value={registration.status} /></td>
                    <td className="px-3 py-3 text-gray-600">{formatWhen(registration.created_at)}</td>
                    <td className="px-3 py-3 text-right">
                      <DangerButton onClick={() => removeRegistration(registration)}>Delete</DangerButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <Pager
            offset={registrationOffset}
            limit={PAGE_SIZE}
            count={registrations.length}
            total={registrationTotal}
            onPage={(nextOffset) => {
              setRegistrationOffset(nextOffset);
              setLoadedTabs((current) => ({ ...current, leads: false }));
            }}
          />
        </Panel>
      ) : null}
    </PageShell>
  );
}
